import {
  ConsentFormConfig,
  ConsentFormFields,
  DEFAULT_CONSENT_CONFIG,
} from '../constants/consent-defaults';

/**
 * Deep-merges a partial saved consent config onto the defaults so storage
 * stays small (admins persist only the fields they customised) while readers
 * always see a fully-populated `ConsentFormConfig`. Future default tweaks
 * therefore flow automatically to orgs that haven't overridden that field.
 */
function mergeConfig(
  base: ConsentFormConfig,
  patch: Record<string, unknown> | null | undefined,
): ConsentFormConfig {
  if (!patch || typeof patch !== 'object') return base;
  const fieldsPatch =
    patch['fields'] && typeof patch['fields'] === 'object'
      ? (patch['fields'] as Record<string, unknown>)
      : {};
  return {
    enabled: typeof patch['enabled'] === 'boolean' ? patch['enabled'] : base.enabled,
    dialog_title:
      typeof patch['dialog_title'] === 'string' && patch['dialog_title'].trim().length > 0
        ? (patch['dialog_title'] as string)
        : base.dialog_title,
    intro_text:
      typeof patch['intro_text'] === 'string' && patch['intro_text'].length > 0
        ? (patch['intro_text'] as string)
        : base.intro_text,
    acknowledgement_text:
      typeof patch['acknowledgement_text'] === 'string' && patch['acknowledgement_text'].length > 0
        ? (patch['acknowledgement_text'] as string)
        : base.acknowledgement_text,
    fields: {
      allergies:
        typeof fieldsPatch['allergies'] === 'boolean'
          ? (fieldsPatch['allergies'] as boolean)
          : base.fields.allergies,
      injuries:
        typeof fieldsPatch['injuries'] === 'boolean'
          ? (fieldsPatch['injuries'] as boolean)
          : base.fields.injuries,
      other_conditions:
        typeof fieldsPatch['other_conditions'] === 'boolean'
          ? (fieldsPatch['other_conditions'] as boolean)
          : base.fields.other_conditions,
      pregnancy:
        typeof fieldsPatch['pregnancy'] === 'boolean'
          ? (fieldsPatch['pregnancy'] as boolean)
          : base.fields.pregnancy,
    } satisfies ConsentFormFields,
  };
}

/**
 * Resolves the effective consent-form config for a (org, branch) settings pair.
 *
 * Branch override wins when `branch.settings.consent_form.override === true`;
 * otherwise the org config is layered onto defaults. Either input may be `null`
 * (e.g. before the org or branch has saved any settings), in which case the
 * defaults shine through unchanged.
 */
export function resolveConsentConfig(
  orgSettings: Record<string, unknown> | null | undefined,
  branchSettings: Record<string, unknown> | null | undefined,
): ConsentFormConfig {
  const branchPatch =
    branchSettings && typeof branchSettings === 'object'
      ? (branchSettings['consent_form'] as Record<string, unknown> | undefined)
      : undefined;
  if (branchPatch && branchPatch['override'] === true) {
    return mergeConfig(DEFAULT_CONSENT_CONFIG, branchPatch);
  }
  const orgPatch =
    orgSettings && typeof orgSettings === 'object'
      ? (orgSettings['consent_form'] as Record<string, unknown> | undefined)
      : undefined;
  return mergeConfig(DEFAULT_CONSENT_CONFIG, orgPatch);
}
